import React, { useEffect, useRef, useState } from "react";
import "./EventLineFields.css";

function cleanTitle(value) {
  return String(value || "").replace(/\s+/g, " ").trim().slice(0, 200);
}

function isYmd(value) {
  return /^\d{4}-\d{2}-\d{2}$/.test(String(value || ""));
}

function isHm(value) {
  return /^([01]\d|2[0-3]):[0-5]\d$/.test(String(value || ""));
}

function formatDayMonth(ymd) {
  const [, month, day] = String(ymd).split("-");
  return `${Number(day)}.${Number(month)}.`;
}

export function formatEventLineWhen(item) {
  if (!item?.date || !isYmd(item.date)) return "Datum fehlt";
  const label = formatDayMonth(item.date);
  if (item.all_day === false && isHm(item.time)) return `${label} · ${item.time}`;
  const end =
    item.end_date && isYmd(item.end_date) && item.end_date !== item.date
      ? formatDayMonth(item.end_date)
      : "";
  return end ? `${label}–${end}` : label;
}

/**
 * Title and date on one extracted line. One field open at a time.
 * onCommit resolves after the change is kept (local or saved).
 */
export default function EventLineFields({
  title = "",
  date = "",
  endDate = "",
  time = "",
  allDay = true,
  editable = false,
  disabled = false,
  onCommit,
}) {
  const [mode, setMode] = useState(null);
  const [draftTitle, setDraftTitle] = useState(title);
  const [draftDate, setDraftDate] = useState(date || "");
  const [draftTime, setDraftTime] = useState(time || "");
  const [draftAllDay, setDraftAllDay] = useState(allDay !== false && !time);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const titleRef = useRef(null);
  const dateRef = useRef(null);
  const busy = useRef(false);
  const ignoreBlur = useRef(false);
  const drafts = useRef({ title, date: date || "", time: time || "", allDay: allDay !== false && !time });
  drafts.current = {
    title: draftTitle,
    date: draftDate,
    time: draftTime,
    allDay: draftAllDay,
  };

  useEffect(() => {
    if (mode) return;
    const nextDate = date || "";
    const nextTime = time || "";
    const nextAllDay = allDay !== false && !time;
    setDraftTitle((current) => (current === title ? current : title));
    setDraftDate((current) => (current === nextDate ? current : nextDate));
    setDraftTime((current) => (current === nextTime ? current : nextTime));
    setDraftAllDay((current) => (current === nextAllDay ? current : nextAllDay));
  }, [title, date, time, allDay, mode]);

  useEffect(() => {
    if (mode === "title") titleRef.current?.focus();
    if (mode === "when") dateRef.current?.focus();
  }, [mode]);

  function closeEditor() {
    ignoreBlur.current = true;
    setMode(null);
  }

  function runCommit(patch) {
    const result = onCommit?.(patch);
    if (!result || typeof result.then !== "function") {
      closeEditor();
      return true;
    }
    busy.current = true;
    setSaving(true);
    setError("");
    return result.then(
      () => {
        closeEditor();
        return true;
      },
      (err) => {
        setError(err instanceof Error ? err.message : "Speichern hat nicht geklappt.");
        return false;
      },
    ).finally(() => {
      busy.current = false;
      setSaving(false);
    });
  }

  function commitTitle(raw) {
    if (busy.current) return false;
    const next = cleanTitle(raw);
    if (!next) {
      setError("Titel fehlt.");
      return false;
    }
    if (next === cleanTitle(title)) {
      setError("");
      closeEditor();
      return true;
    }
    setError("");
    return runCommit({ title: next });
  }

  function commitWhen() {
    if (busy.current) return false;
    const nextDate = drafts.current.date;
    const nextAllDay = drafts.current.allDay;
    const nextClock = drafts.current.time;
    if (!isYmd(nextDate)) {
      setError("Datum fehlt.");
      return false;
    }
    const timed = !nextAllDay;
    if (timed && !isHm(nextClock)) {
      setError(nextClock ? "Ungültige Uhrzeit." : "Uhrzeit fehlt.");
      return false;
    }
    const prevAllDay = allDay !== false && !time;
    const prevTime = prevAllDay ? "" : time || "";
    const nextTime = timed ? nextClock : "";
    if (nextDate === date && timed === !prevAllDay && nextTime === prevTime) {
      setError("");
      closeEditor();
      return true;
    }
    const patch = timed
      ? { date: nextDate, time: nextClock, all_day: false }
      : { date: nextDate, time: null, all_day: true };
    setError("");
    return runCommit(patch);
  }

  function openTitle() {
    if (!editable || disabled || saving) return;
    if (mode === "when") {
      const pending = commitWhen();
      if (!pending || typeof pending.then === "function") return;
    }
    setError("");
    drafts.current = { ...drafts.current, title };
    setDraftTitle(title);
    setMode("title");
  }

  function openWhen() {
    if (!editable || disabled || saving) return;
    if (mode === "title") {
      const pending = commitTitle(drafts.current.title);
      if (!pending || typeof pending.then === "function") return;
    }
    const nextAllDay = allDay !== false && !time;
    setError("");
    drafts.current = {
      ...drafts.current,
      date: date || "",
      time: time || "",
      allDay: nextAllDay,
    };
    setDraftDate(date || "");
    setDraftTime(time || "");
    setDraftAllDay(nextAllDay);
    setMode("when");
  }

  const displayTitle = title || "(ohne Titel)";
  const whenLabel = formatEventLineWhen({ date, end_date: endDate, time, all_day: allDay });

  if (!editable) {
    return (
      <span className="event-line-fields">
        <span className="event-line-title">{displayTitle}</span>
        <span className="event-line-when">{whenLabel}</span>
      </span>
    );
  }

  return (
    <span className="event-line-fields">
      {mode === "title" ? (
        <input
          ref={titleRef}
          className="event-line-input"
          aria-label="Titel"
          value={draftTitle}
          disabled={saving}
          maxLength={200}
          onChange={(e) => {
            drafts.current.title = e.target.value;
            setDraftTitle(e.target.value);
            if (error) setError("");
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              commitTitle(e.currentTarget.value);
            }
            if (e.key === "Escape") {
              e.preventDefault();
              setError("");
              closeEditor();
            }
          }}
          onBlur={(e) => {
            if (ignoreBlur.current) {
              ignoreBlur.current = false;
              return;
            }
            commitTitle(e.target.value);
          }}
        />
      ) : (
        <button
          type="button"
          className="event-line-title event-line-hit"
          disabled={disabled || saving}
          onMouseDown={(e) => e.preventDefault()}
          onClick={openTitle}
        >
          {displayTitle}
        </button>
      )}
      {mode === "when" ? (
        <span
          className="event-line-when-edit"
          onBlur={(e) => {
            if (e.currentTarget.contains(e.relatedTarget)) return;
            if (ignoreBlur.current) {
              ignoreBlur.current = false;
              return;
            }
            commitWhen();
          }}
        >
          <input
            ref={dateRef}
            className="event-line-input event-line-input--date"
            type="date"
            aria-label="Datum"
            value={draftDate}
            disabled={saving}
            onChange={(e) => {
              drafts.current.date = e.target.value;
              setDraftDate(e.target.value);
              if (error) setError("");
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                commitWhen();
              }
              if (e.key === "Escape") {
                e.preventDefault();
                setError("");
                closeEditor();
              }
            }}
          />
          <label className="event-line-all-day">
            <input
              type="checkbox"
              checked={draftAllDay}
              disabled={saving}
              onChange={(e) => {
                drafts.current.allDay = e.target.checked;
                if (e.target.checked) drafts.current.time = "";
                setDraftAllDay(e.target.checked);
                if (e.target.checked) setDraftTime("");
                if (error) setError("");
              }}
            />
            <span>Ganztägig</span>
          </label>
          {!draftAllDay ? (
            <input
              className="event-line-input event-line-input--time"
              type="time"
              aria-label="Uhrzeit"
              value={draftTime}
              disabled={saving}
              onChange={(e) => {
                drafts.current.time = e.target.value;
                setDraftTime(e.target.value);
                if (error) setError("");
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  commitWhen();
                }
              }}
            />
          ) : null}
        </span>
      ) : (
        <button
          type="button"
          className="event-line-when event-line-hit"
          disabled={disabled || saving}
          onMouseDown={(e) => e.preventDefault()}
          onClick={openWhen}
        >
          {whenLabel}
        </button>
      )}
      {error ? (
        <span className="event-line-error" role="alert">
          {error}
        </span>
      ) : null}
    </span>
  );
}
